import { CaptionedBlockComponent } from '@blocksuite/affine-components/caption';
import { PeekViewProvider } from '@blocksuite/affine-components/peek';
import { toast } from '@blocksuite/affine-components/toast';
import type {
  DatabaseBlockModel,
  DatabaseViewRefBlockModel,
} from '@blocksuite/affine-model';
import {
  NotificationProvider,
  type TelemetryEventMap,
  TelemetryProvider,
} from '@blocksuite/affine-shared/services';
import {
  createRecordDetail,
  createUniComponentFromWebComponent,
  DataViewRootUILogic,
  type DataViewSelection,
  type DataViewWidget,
  type DataViewWidgetProps,
  defineUniComponent,
  renderUniLit,
  uniMap,
} from '@blocksuite/data-view';
import { widgetPresets } from '@blocksuite/data-view/widget-presets';
import { computed, signal } from '@preact/signals-core';
import { html } from 'lit';

import { popSideDetail } from '../components/layout.js';
import {
  databaseContentStyles,
  databaseHeaderContainerStyles,
} from '../database-block-styles.js';
import { BlockRenderer } from '../detail-panel/block-renderer.js';
import { NoteRenderer } from '../detail-panel/note-renderer.js';
import { createDatabaseDataSource } from '../utils/create-data-view-logic.js';
import { popDatabaseViewRefPicker } from './picker.js';

/**
 * Renders a single view of a database that lives on another page (or
 * elsewhere on the same page).
 *
 * The block only stores a reference (`sourceDocId`, `sourceBlockId`,
 * `viewId`). Editing goes straight into the source database, because the
 * data source is built on the source model — there is no copy of the data.
 */
export class DatabaseViewRefBlockComponent extends CaptionedBlockComponent<DatabaseViewRefBlockModel> {
  private _dataSourceKey = '';
  private _rootLogic: DataViewRootUILogic | null = null;
  private readonly _loadAttempted = new Set<string>();

  private readonly viewSelection$ = signal<DataViewSelection | undefined>();

  /**
   * Resolve the referenced database model, loading the source doc lazily.
   */
  private readonly sourceModel$ = computed<DatabaseBlockModel | null>(() => {
    const { sourceDocId, sourceBlockId } = this.model.props;
    if (!sourceBlockId) return null;
    const docId = sourceDocId || this.store.id;
    if (docId === this.store.id) {
      const block = this.store.getBlock(sourceBlockId)?.model;
      return block?.flavour === 'affine:database'
        ? (block as DatabaseBlockModel)
        : null;
    }
    const doc = this.std.workspace.getDoc(docId);
    if (!doc) return null;
    if (!doc.loaded) {
      if (!this._loadAttempted.has(docId)) {
        this._loadAttempted.add(docId);
        queueMicrotask(() => {
          doc.load(() => this.requestUpdate());
        });
      }
      return null;
    }
    const block = doc.getStore({ id: doc.id }).getBlock(sourceBlockId)?.model;
    return block?.flavour === 'affine:database'
      ? (block as unknown as DatabaseBlockModel)
      : null;
  });

  /**
   * Header without the view bar — a view ref is pinned to one view.
   */
  private readonly headerWidget: DataViewWidget = defineUniComponent(
    (props: DataViewWidgetProps) => html`
      <div class="${databaseHeaderContainerStyles}">
        ${renderUniLit(widgetPresets.quickSettingBar, props)}
      </div>
    `
  );

  private readonly setSelection = (selection?: DataViewSelection) => {
    this.viewSelection$.value = selection;
  };

  /**
   * Build (and cache) the data view logic for the current reference.
   * Rebuilt whenever the referenced database or view changes.
   */
  private getRootLogic(model: DatabaseBlockModel): DataViewRootUILogic | null {
    const viewId = this.model.props.viewId;
    const key = `${model.store.id}/${model.id}/${viewId}`;
    if (this._rootLogic && this._dataSourceKey === key) {
      return this._rootLogic;
    }

    const dataSource = createDatabaseDataSource(model, this.host);
    if (viewId && dataSource.viewManager.viewGet(viewId)) {
      // `setCurrentView` is in-memory only, so pinning the view here does
      // not write anything back to the source document.
      dataSource.viewManager.setCurrentView(viewId);
    } else if (viewId) {
      // referenced view was deleted
      return null;
    }

    const rootLogic = new DataViewRootUILogic({
      virtualPadding$: signal(0),
      bindHotkey: hotkeys => ({
        dispose: this.host.event.bindHotkey(hotkeys, {
          blockId: this.blockId,
        }),
      }),
      handleEvent: (name, handler) => ({
        dispose: this.host.event.add(name, handler, { blockId: this.blockId }),
      }),
      selection$: this.viewSelection$,
      setSelection: this.setSelection,
      dataSource,
      headerWidget: this.headerWidget,
      clipboard: this.std.clipboard,
      dnd: this.std.dnd,
      notification: {
        toast: message => {
          const notification = this.std.getOptional(NotificationProvider);
          if (notification) {
            notification.toast(message);
          } else {
            toast(this.host, message);
          }
        },
      },
      eventTrace: (key, params) => {
        this.std.getOptional(TelemetryProvider)?.track(key, {
          ...(params as TelemetryEventMap[typeof key]),
          blockId: this.blockId,
        });
      },
      detailPanelConfig: {
        // the record detail belongs to the *source* database, so the peek
        // view has to be opened against the source doc
        openDetailPanel: (target, data) => {
          const template = createRecordDetail({
            ...data,
            openDoc: () => {},
            detail: {
              header: uniMap(
                createUniComponentFromWebComponent(BlockRenderer),
                props => ({ ...props, host: this.host })
              ),
              note: uniMap(
                createUniComponentFromWebComponent(NoteRenderer),
                props => ({ ...props, model, host: this.host })
              ),
            },
          });
          const peekViewService = this.std.getOptional(PeekViewProvider);
          if (!peekViewService) {
            return popSideDetail(template);
          }
          return peekViewService.peek({ target, template });
        },
      },
    });

    this._dataSourceKey = key;
    this._rootLogic = rootLogic;
    return rootLogic;
  }

  private renderPlaceholder(text: string, pickable = true) {
    const openPicker = (e: MouseEvent) => {
      if (!pickable) return;
      popDatabaseViewRefPicker(
        this.host,
        this.model,
        e.currentTarget as HTMLElement
      );
    };
    return html`<div class="${databaseContentStyles}" contenteditable="false">
      <div
        style="padding: 12px; opacity: 0.6; font-size: 14px; cursor: ${pickable
          ? 'pointer'
          : 'default'};"
        @click="${openPicker}"
      >
        ${text}
      </div>
    </div>`;
  }

  override renderBlock() {
    const { sourceBlockId } = this.model.props;
    if (!sourceBlockId) {
      return this.renderPlaceholder('No database selected');
    }
    const model = this.sourceModel$.value;
    if (!model) {
      return this.renderPlaceholder('Loading database…', false);
    }
    const rootLogic = this.getRootLogic(model);
    if (!rootLogic) {
      return this.renderPlaceholder('The referenced view no longer exists');
    }
    const title = model.props.title?.toString() || 'Untitled database';
    return html`
      <div contenteditable="false" class="${databaseContentStyles}">
        <div style="font-size: 12px; opacity: 0.6; padding: 4px 0;">
          ${title}
        </div>
        ${rootLogic.render()}
      </div>
    `;
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._rootLogic = null;
    this._dataSourceKey = '';
  }

  override accessor useZeroWidth = true;
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-database-view-ref': DatabaseViewRefBlockComponent;
  }
}
